import {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {IPhoneFieldValue} from "@pg-design/phone-input-module";
import {FormProviderRHF, PhoneFieldControlled, useFormRHF} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {REGISTRATION_WIZARD_TEST_IDS} from "@pg-mono/test-ids";

import {IRPStore} from "../../../app/rp_reducer";
import {leadModalZindex} from "../../../application/constants/lead/lead_modal_zindex";
import {validationMessages} from "../../../form/utils/validation_messages";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {useIsRegistrationWizardSmsLogin} from "../../hooks/use_is_registration_wizard_sms_login";
import {useRegistrationWizardAccountPhone} from "../../hooks/use_registration_wizard_account_phone";
import {useRegistrationWizardSmsLoginCode} from "../../hooks/use_registration_wizard_sms_login_code";
import {useRegistrationWizardTrackingContext} from "../../hooks/use_registration_wizard_tracking_context";
import {saveWizardDraft} from "../../state/registration_flow_slice";
import {trackRegistrationWizardButtonClick} from "../../tracking/track_registration_wizard_events";
import {getSmsLoginAccountMismatchMessage} from "../../utils/get_sms_login_account_mismatch_message";
import {getPhoneNumberLengthMessage, getProfilePhoneValue, hasValidPhoneNumberLength, isSamePhone} from "../../utils/registration_wizard_phone";
import {RegisterFormPhoneVerificationInfo} from "../auth_card/RegisterFormPhoneVerificationInfo";
import {SmsLoginAccountMismatchMessage} from "../auth_card/SmsLoginAccountMismatchMessage";
import {RegistrationWizardHotlineInfo} from "./RegistrationWizardHotlineInfo";
import {RegistrationWizardStepLayout} from "./RegistrationWizardStepLayout";

interface IProps {
    titleId: string;
    onAdvance: () => Promise<void>;
}

interface IPhoneFormValues {
    phone: IPhoneFieldValue;
}

const PHONE_CODE_MENU_Z_INDEX = leadModalZindex + 1;
const EMPTY_PHONE: IPhoneFieldValue = {code: 48, number: ""};

const phoneValidationSchema = Yup.object({
    phone: Yup.object({
        code: Yup.number().required(validationMessages.required),
        number: Yup.string()
            .required(validationMessages.required)
            .matches(/^[\d\s-]+$/, "Podaj poprawny numer telefonu")
            .test("phone-number-length", (number, context) => {
                const code: number = context.parent.code;

                return !number || hasValidPhoneNumberLength({code, number}) || context.createError({message: getPhoneNumberLengthMessage(code)});
            })
    })
});

export const RegistrationWizardPhoneStep = (props: IProps) => {
    const {titleId, onAdvance} = props;
    const dispatch = useAppDispatch();
    const {wizardDrafts} = useAppSelector((state) => state.registrationFlow);
    const isSmsLoginPath = useIsRegistrationWizardSmsLogin();
    const {data: userProfile} = useGetUserProfileQuery();
    const countryCodes = useSelector((state: IRPStore) => state.variables.data?.country_codes ?? []);
    const trackingContext = useRegistrationWizardTrackingContext();
    const profilePhone = getProfilePhoneValue(userProfile?.phone);

    const initialPhoneRef = useRef<IPhoneFieldValue>(wizardDrafts.phone ?? profilePhone ?? EMPTY_PHONE);
    const [isSmsLimitReached, setIsSmsLimitReached] = useState(false);
    const [isAccountMismatch, setIsAccountMismatch] = useState(false);

    const form = useFormRHF<IPhoneFormValues>({
        defaultValues: {phone: initialPhoneRef.current},
        resolver: yupResolver(phoneValidationSchema),
        mode: "onSubmit"
    });
    const {isSubmitting} = form.formState;
    const typedPhone = form.watch("phone");

    useEffect(() => {
        setIsSmsLimitReached(false);
        setIsAccountMismatch(false);
    }, [typedPhone.code, typedPhone.number]);

    useEffect(() => {
        return () => {
            const phone = form.getValues("phone");

            if (!isSamePhone(phone, initialPhoneRef.current)) {
                dispatch(saveWizardDraft({phone}));
            }
        };
    }, []);

    const setPhoneError = (message: string) => {
        form.setError("phone.number", {type: "server", message});
    };

    const {sendSmsLoginCode} = useRegistrationWizardSmsLoginCode({setPhoneError, setIsSmsLimitReached, setIsAccountMismatch});
    const {saveAccountPhone} = useRegistrationWizardAccountPhone({profilePhone, setPhoneError, onAdvance});

    const onSubmit = async (values: IPhoneFormValues) => {
        trackRegistrationWizardButtonClick(trackingContext, "rp_auth_send_sms");
        setIsSmsLimitReached(false);
        setIsAccountMismatch(false);

        if (isSmsLoginPath) {
            await sendSmsLoginCode(values.phone);
            return;
        }

        await saveAccountPhone(values.phone);
    };

    return (
        <RegistrationWizardStepLayout
            titleId={titleId}
            title="Podaj numer telefonu"
            description="Numer telefonu jest przypisany do konta i służy późniejszemu logowaniu."
        >
            <FormProviderRHF {...form}>
                <form className={formStyle} onSubmit={form.handleSubmit(onSubmit)} noValidate>
                    <div className={phoneFieldStyle}>
                        <PhoneFieldControlled
                            name="phone"
                            codes={countryCodes}
                            placeholder="Telefon"
                            maxMenuHeight={150}
                            menuPortal={typeof document === "undefined" ? undefined : {target: document.body, zIndex: PHONE_CODE_MENU_Z_INDEX}}
                            dataTestId={REGISTRATION_WIZARD_TEST_IDS.PHONE_INPUT}
                            disabled={isSubmitting}
                            fieldState={isAccountMismatch ? "error" : undefined}
                            afterInput={
                                <>
                                    <RegisterFormPhoneVerificationInfo />
                                    {isAccountMismatch && <SmsLoginAccountMismatchMessage message={getSmsLoginAccountMismatchMessage()} />}
                                </>
                            }
                        />
                        {isSmsLimitReached && (
                            <Text as="p" variant="info_txt_1" className={hotlineInfoStyle}>
                                <RegistrationWizardHotlineInfo />
                            </Text>
                        )}
                    </div>
                    <Button
                        type="submit"
                        variant="filled_primary"
                        fullWidth
                        isLoading={isSubmitting}
                        disabled={isSubmitting}
                        dataTestId={REGISTRATION_WIZARD_TEST_IDS.PHONE_SUBMIT_BUTTON}
                    >
                        WYŚLIJ KOD SMS
                    </Button>
                </form>
            </FormProviderRHF>
        </RegistrationWizardStepLayout>
    );
};

const formStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 3
});

const phoneFieldStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1
});

const hotlineInfoStyle = css({
    color: "var(--colors-gray-700)"
});
import {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {IPhoneFieldValue} from "@pg-design/phone-input-module";
import {FormProviderRHF, PhoneFieldControlled, useFormRHF} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {REGISTRATION_WIZARD_TEST_IDS} from "@pg-mono/test-ids";

import {IRPStore} from "../../../app/rp_reducer";
import {leadModalZindex} from "../../../application/constants/lead/lead_modal_zindex";
import {validationMessages} from "../../../form/utils/validation_messages";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {useIsRegistrationWizardSmsLogin} from "../../hooks/use_is_registration_wizard_sms_login";
import {useRegistrationWizardAccountPhone} from "../../hooks/use_registration_wizard_account_phone";
import {useRegistrationWizardSmsLoginCode} from "../../hooks/use_registration_wizard_sms_login_code";
import {useRegistrationWizardTrackingContext} from "../../hooks/use_registration_wizard_tracking_context";
import {saveWizardDraft} from "../../state/registration_flow_slice";
import {trackRegistrationWizardButtonClick} from "../../tracking/track_registration_wizard_events";
import {getSmsLoginAccountMismatchMessage} from "../../utils/get_sms_login_account_mismatch_message";
import {getPhoneNumberLengthMessage, getProfilePhoneValue, hasValidPhoneNumberLength, isSamePhone} from "../../utils/registration_wizard_phone";
import {RegisterFormPhoneVerificationInfo} from "../auth_card/RegisterFormPhoneVerificationInfo";
import {SmsLoginAccountMismatchMessage} from "../auth_card/SmsLoginAccountMismatchMessage";
import {RegistrationWizardHotlineInfo} from "./RegistrationWizardHotlineInfo";
import {RegistrationWizardStepLayout} from "./RegistrationWizardStepLayout";

interface IProps {
    titleId: string;
    onAdvance: () => Promise<void>;
}

interface IPhoneFormValues {
    phone: IPhoneFieldValue;
}

const PHONE_CODE_MENU_Z_INDEX = leadModalZindex + 1;
const EMPTY_PHONE: IPhoneFieldValue = {code: 48, number: ""};

const phoneValidationSchema = Yup.object({
    phone: Yup.object({
        code: Yup.number().required(validationMessages.required),
        number: Yup.string()
            .required(validationMessages.required)
            .matches(/^[\d\s-]+$/, "Podaj poprawny numer telefonu")
            .test("phone-number-length", (number, context) => {
                const code: number = context.parent.code;

                return !number || hasValidPhoneNumberLength({code, number}) || context.createError({message: getPhoneNumberLengthMessage(code)});
            })
    })
});

export const RegistrationWizardPhoneStep = (props: IProps) => {
    const {titleId, onAdvance} = props;
    const dispatch = useAppDispatch();
    const {wizardDrafts} = useAppSelector((state) => state.registrationFlow);
    const isSmsLoginPath = useIsRegistrationWizardSmsLogin();
    const {data: userProfile} = useGetUserProfileQuery();
    const countryCodes = useSelector((state: IRPStore) => state.variables.data?.country_codes ?? []);
    const trackingContext = useRegistrationWizardTrackingContext();
    const profilePhone = getProfilePhoneValue(userProfile?.phone);

    const initialPhoneRef = useRef<IPhoneFieldValue>(wizardDrafts.phone ?? profilePhone ?? EMPTY_PHONE);
    const [isSmsLimitReached, setIsSmsLimitReached] = useState(false);
    const [isAccountMismatch, setIsAccountMismatch] = useState(false);

    const form = useFormRHF<IPhoneFormValues>({
        defaultValues: {phone: initialPhoneRef.current},
        resolver: yupResolver(phoneValidationSchema),
        mode: "onSubmit"
    });
    const {isSubmitting} = form.formState;
    const typedPhone = form.watch("phone");

    useEffect(() => {
        setIsSmsLimitReached(false);
        setIsAccountMismatch(false);
    }, [typedPhone.code, typedPhone.number]);

    useEffect(() => {
        return () => {
            const phone = form.getValues("phone");

            if (!isSamePhone(phone, initialPhoneRef.current)) {
                dispatch(saveWizardDraft({phone}));
            }
        };
    }, []);

    const setPhoneError = (message: string) => {
        form.setError("phone.number", {type: "server", message});
    };

    const {sendSmsLoginCode} = useRegistrationWizardSmsLoginCode({setPhoneError, setIsSmsLimitReached, setIsAccountMismatch});
    const {saveAccountPhone} = useRegistrationWizardAccountPhone({profilePhone, setPhoneError, onAdvance});

    const onSubmit = async (values: IPhoneFormValues) => {
        trackRegistrationWizardButtonClick(trackingContext, "rp_auth_send_sms");
        setIsSmsLimitReached(false);
        setIsAccountMismatch(false);

        if (isSmsLoginPath) {
            await sendSmsLoginCode(values.phone);
            return;
        }

        await saveAccountPhone(values.phone);
    };

    return (
        <RegistrationWizardStepLayout
            titleId={titleId}
            title="Podaj numer telefonu"
            description="Numer telefonu jest przypisany do konta i służy późniejszemu logowaniu."
        >
            <FormProviderRHF {...form}>
                <form className={formStyle} onSubmit={form.handleSubmit(onSubmit)} noValidate>
                    <div className={phoneFieldStyle}>
                        <PhoneFieldControlled
                            name="phone"
                            codes={countryCodes}
                            placeholder="Telefon"
                            maxMenuHeight={150}
                            menuPortal={typeof document === "undefined" ? undefined : {target: document.body, zIndex: PHONE_CODE_MENU_Z_INDEX}}
                            dataTestId={REGISTRATION_WIZARD_TEST_IDS.PHONE_INPUT}
                            disabled={isSubmitting}
                            fieldState={isAccountMismatch ? "error" : undefined}
                            afterInput={
                                <>
                                    <RegisterFormPhoneVerificationInfo />
                                    {isAccountMismatch && <SmsLoginAccountMismatchMessage message={getSmsLoginAccountMismatchMessage()} />}
                                </>
                            }
                        />
                        {isSmsLimitReached && (
                            <Text as="p" variant="info_txt_1" className={hotlineInfoStyle}>
                                <RegistrationWizardHotlineInfo />
                            </Text>
                        )}
                    </div>
                    <Button
                        type="submit"
                        variant="filled_primary"
                        fullWidth
                        isLoading={isSubmitting}
                        disabled={isSubmitting}
                        dataTestId={REGISTRATION_WIZARD_TEST_IDS.PHONE_SUBMIT_BUTTON}
                    >
                        WYŚLIJ KOD SMS
                    </Button>
                </form>
            </FormProviderRHF>
        </RegistrationWizardStepLayout>
    );
};

const formStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 3
});

const phoneFieldStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1
});

const hotlineInfoStyle = css({
    color: "var(--colors-gray-700)"
});
